import React, { useMemo, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { ChartContainer } from '@mui/x-charts/ChartContainer'
import { ChartsAxis } from '@mui/x-charts/ChartsAxis'
import { ChartsGrid } from '@mui/x-charts/ChartsGrid'
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip'
import { LinePlot, MarkPlot } from '@mui/x-charts/LineChart'
import { ScatterPlot } from '@mui/x-charts/ScatterChart'
import type { GraybookCardData } from './useAssistantChat'
import { GraybookComparisonChart } from './GraybookComparisonChart'
import { BRAND_NAVY, BRAND_NAVY_DARK, BRAND_RED, TOUCH_MIN } from 'core/theme.tokens'

const BUY_IN_DOT = '#2e7d32'

function formatMoney(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `$${Math.round(value).toLocaleString('en-US')}`
}

function formatMiles(value: number): string {
  if (value >= 1000) {
    return `${(value / 1000).toLocaleString('en-US', { maximumFractionDigits: 0 })}K`
  }
  return value.toLocaleString('en-US')
}

function vehicleLabel(data: GraybookCardData): string {
  const stockOrVin = data.vehicle?.stockNumber || data.vehicle?.vin || data.vehicle?.id
  const ymm = [data.vehicle?.year, data.vehicle?.make, data.vehicle?.model]
    .filter(Boolean)
    .join(' ')
  if (stockOrVin && ymm) return `${stockOrVin} · ${ymm}`
  return stockOrVin || ymm || 'Vehicle'
}

function isPriced(data: GraybookCardData): boolean {
  return data.retail != null && Number.isFinite(data.retail)
}

interface GraybookCardProps {
  data: GraybookCardData
  /** Controlled expand when used in a list. */
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  defaultExpanded?: boolean
  dense?: boolean
}

/** Collapsed summary + expandable chart / breakdown / reasoning. */
export const GraybookCard: React.FC<GraybookCardProps> = ({
  data,
  expanded: expandedProp,
  onExpandedChange,
  defaultExpanded = false,
  dense = false,
}) => {
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded)
  const expanded = expandedProp ?? uncontrolledExpanded
  const setExpanded = (next: boolean) => {
    onExpandedChange?.(next)
    if (expandedProp === undefined) setUncontrolledExpanded(next)
  }

  const chart = data.chart
  const priced = isPriced(data)
  const label = vehicleLabel(data)

  const series = useMemo(() => {
    if (!chart?.series?.length) return []
    return chart.series
      .filter(s => s.data.some(v => v != null))
      .map(s => ({
        type: 'line' as const,
        id: s.id,
        label:
          s.id === 'ols'
            ? 'OLS retail by mileage'
            : 'Formula retail by mileage (if this truck had more/fewer miles)',
        data: s.data.map(v => (v == null ? null : v)),
        color: s.id === 'ols' ? BRAND_NAVY : BRAND_RED,
        showMark: false,
        curve: 'linear' as const,
      }))
  }, [chart])

  const currentScatter = useMemo(() => {
    if (!chart?.currentPoint) return null
    const miles = formatMiles(chart.currentPoint.mileage)
    return {
      type: 'scatter' as const,
      id: 'current-retail',
      label: `Sell now (${miles} mi → ${formatMoney(chart.currentPoint.retail)})`,
      data: [
        {
          id: 0,
          x: chart.currentPoint.mileage,
          y: chart.currentPoint.retail,
        },
      ],
      color: BRAND_NAVY_DARK,
      markerSize: 8,
    }
  }, [chart])

  const buyInScatter = useMemo(() => {
    const wholesale = chart?.currentPoint?.wholesale
    if (wholesale == null || !Number.isFinite(wholesale) || !chart?.currentPoint) return null
    const miles = formatMiles(chart.currentPoint.mileage)
    return {
      type: 'scatter' as const,
      id: 'current-buyin',
      label: `Buy-in now (${miles} mi → ${formatMoney(wholesale)})`,
      data: [
        {
          id: 1,
          x: chart.currentPoint.mileage,
          y: wholesale,
        },
      ],
      color: BUY_IN_DOT,
      markerSize: 8,
    }
  }, [chart])

  const chartLegend = useMemo(() => {
    const items: Array<{ color: string; label: string; shape: 'line' | 'dot' }> = []
    if (series.some(s => s.id === 'formula' || s.id === 'ols')) {
      items.push({
        color: series[0]?.color || BRAND_RED,
        label: 'Red line = Gray Book retail (sell) as mileage changes',
        shape: 'line',
      })
    }
    if (currentScatter && chart?.currentPoint) {
      items.push({
        color: BRAND_NAVY_DARK,
        label: `Dark dot = sell today (${formatMiles(chart.currentPoint.mileage)} mi at ${formatMoney(chart.currentPoint.retail)})`,
        shape: 'dot',
      })
    }
    if (buyInScatter && chart?.currentPoint?.wholesale != null) {
      items.push({
        color: BUY_IN_DOT,
        label: `Green dot = buy-in today (${formatMiles(chart.currentPoint.mileage)} mi at ${formatMoney(chart.currentPoint.wholesale)})`,
        shape: 'dot',
      })
    }
    return items
  }, [series, currentScatter, buyInScatter, chart])

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_, next) => setExpanded(next)}
      sx={{
        mt: dense ? 0.5 : 1,
        border: '1px solid',
        borderColor: priced ? 'divider' : 'action.disabledBackground',
        borderRadius: '8px !important',
        bgcolor: 'background.paper',
        '&:before': { display: 'none' },
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore fontSize={dense ? 'small' : 'medium'} />}
        sx={{
          minHeight: dense ? 40 : TOUCH_MIN,
          px: dense ? 1 : 1.25,
          '& .MuiAccordionSummary-content': {
            my: dense ? 0.75 : 1,
            alignItems: 'center',
            gap: dense ? 0.75 : 1,
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction='row' alignItems='center' gap={0.75} sx={{ mb: 0.5, minWidth: 0 }}>
            <Typography
              variant='subtitle2'
              sx={{
                fontWeight: 700,
                minWidth: 0,
                fontSize: dense ? '0.8125rem' : undefined,
                lineHeight: dense ? 1.3 : undefined,
              }}
              noWrap
              title={label}
            >
              {label}
            </Typography>
            {data.method ? (
              <Chip
                size='small'
                label={data.method === 'OLS Model' ? 'OLS' : 'Formula'}
                color='primary'
                variant='outlined'
                sx={{ height: 20, flexShrink: 0, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
              />
            ) : (
              <Chip
                size='small'
                label='Needs data'
                variant='outlined'
                sx={{ height: 20, flexShrink: 0, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
              />
            )}
          </Stack>
          <Stack direction='row' flexWrap='wrap' gap={0.5}>
            <Chip
              size='small'
              label={`Sell ${formatMoney(data.retail)}`}
              sx={{
                fontWeight: 600,
                height: 22,
                '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
              }}
            />
            {data.wholesale != null ? (
              <Chip
                size='small'
                color='success'
                variant='outlined'
                label={`Buy-in ${formatMoney(data.wholesale)}`}
                sx={{ height: 22, fontWeight: 600, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
              />
            ) : null}
            {data.vehicle?.sellerAskingPrice != null ? (
              <Chip
                size='small'
                variant='outlined'
                label={`Buy ${formatMoney(data.vehicle.sellerAskingPrice)}`}
                sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
              />
            ) : null}
            {data.dealQuality ? (
              <Chip
                size='small'
                label={data.dealQuality.label}
                sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
              />
            ) : null}
            {data.vehicle?.opticListPrice != null ? (
              <Chip
                size='small'
                variant='outlined'
                label={`List ${formatMoney(data.vehicle.opticListPrice)}`}
                sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
              />
            ) : null}
            {chart && (series.length > 0 || chart.currentPoint) ? (
              <Chip
                size='small'
                variant='outlined'
                label='Mileage curve'
                sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
              />
            ) : null}
          </Stack>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
        {!priced ? (
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
            {data.reasoning[0] ||
              data.error ||
              'Missing attributes to price. Update inventory specs or use the Gray Book calculator.'}
          </Typography>
        ) : null}

        {chart && (series.length > 0 || currentScatter) ? (
          <Box sx={{ width: '100%', mb: 1.25, mt: priced ? 0 : 1 }}>
            <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
              Mileage depreciation
            </Typography>
            {chartLegend.length > 0 ? (
              <Stack spacing={0.35} sx={{ mb: 0.75 }}>
                {chartLegend.map(item => (
                  <Stack key={item.label} direction='row' alignItems='center' gap={0.75}>
                    {item.shape === 'line' ? (
                      <Box
                        sx={{
                          width: 18,
                          height: 0,
                          borderTop: '3px solid',
                          borderColor: item.color,
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: item.color,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Typography variant='caption' color='text.secondary'>
                      {item.label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            ) : null}
            {series.length > 0 || currentScatter || buyInScatter ? (
              <Box sx={{ width: '100%', height: 180 }}>
                <ChartContainer
                  width={undefined}
                  height={180}
                  series={
                    [
                      ...series,
                      ...(currentScatter ? [currentScatter] : []),
                      ...(buyInScatter ? [buyInScatter] : []),
                    ] as never
                  }
                  xAxis={[
                    {
                      data: chart.mileagePoints,
                      valueFormatter: (v: number | null) => (v == null ? '' : formatMiles(v)),
                      label: 'Mileage',
                    },
                  ]}
                  yAxis={[
                    {
                      valueFormatter: (v: number | null) => (v == null ? '' : formatMoney(v)),
                      width: 56,
                    },
                  ]}
                  margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
                >
                  <ChartsGrid horizontal />
                  <LinePlot />
                  <MarkPlot />
                  {currentScatter || buyInScatter ? <ScatterPlot /> : null}
                  <ChartsAxis />
                  <ChartsTooltip trigger='axis' />
                </ChartContainer>
              </Box>
            ) : null}
            <Typography variant='caption' color='text.disabled' sx={{ display: 'block', mt: 0.5 }}>
              Red line = retail (sell) vs mileage. Green dot = Gray Book buy-in; dark dot = sell today.
              Seller asking (bought / can buy for) is in the Buy chip and breakdown when set on the truck.
            </Typography>
          </Box>
        ) : null}

        {data.breakdown.length > 0 ? (
          <Box sx={{ mb: 1 }}>
            <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600 }}>
              Breakdown
            </Typography>
            <Stack spacing={0.25} sx={{ mt: 0.5 }}>
              {data.breakdown.map(row => (
                <Stack
                  key={`${row.label}:${row.amount}`}
                  direction='row'
                  justifyContent='space-between'
                  gap={1}
                >
                  <Typography variant='caption' color='text.secondary'>
                    {row.label}
                  </Typography>
                  <Typography variant='caption' sx={{ fontWeight: 600 }}>
                    {formatMoney(row.amount)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        ) : null}

        {data.reasoning.length > 0 && priced ? (
          <Box>
            <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600 }}>
              Reasoning
            </Typography>
            <Box component='ul' sx={{ m: 0, mt: 0.5, pl: 2 }}>
              {data.reasoning.map(line => (
                <Typography
                  key={line}
                  component='li'
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'list-item' }}
                >
                  {line}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null}

        {data.note && priced ? (
          <Typography variant='caption' color='text.disabled' sx={{ display: 'block', mt: 1 }}>
            {data.note}
          </Typography>
        ) : null}
      </AccordionDetails>
    </Accordion>
  )
}

interface GraybookCardListProps {
  cards: GraybookCardData[]
  messageId: string
}

/**
 * Groups priced vs unpriced Gray Book cards.
 * Priced cards: accordion (collapsed by default; first expands).
 * Unpriced: one nested accordion so empty results don't flood the chat.
 */
export const GraybookCardList: React.FC<GraybookCardListProps> = ({ cards, messageId }) => {
  const priced = cards.filter(isPriced)
  const unpriced = cards.filter(c => !isPriced(c))
  const [expandedId, setExpandedId] = useState<string | null>(
    priced.length === 1 ? `${messageId}:0` : null,
  )
  const [unpricedOpen, setUnpricedOpen] = useState(false)

  if (cards.length === 0) return null

  return (
    <Box sx={{ mt: 1, width: '100%' }}>
      {priced.length > 1 ? <GraybookComparisonChart cards={priced} /> : null}

      {priced.length > 0 ? (
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ fontWeight: 600, display: 'block', mb: 0.5, px: 0.25 }}
        >
          Gray Book · {priced.length} priced
          {priced.length > 1 ? ' · expand a row for details' : ''}
        </Typography>
      ) : null}

      {priced.map((card, index) => {
        const id = `${messageId}:priced:${index}`
        return (
          <GraybookCard
            key={id}
            data={card}
            dense
            expanded={expandedId === id}
            onExpandedChange={next => setExpandedId(next ? id : null)}
          />
        )
      })}

      {unpriced.length > 0 ? (
        <Accordion
          disableGutters
          elevation={0}
          expanded={unpricedOpen}
          onChange={(_, next) => setUnpricedOpen(next)}
          sx={{
            mt: 1,
            border: '1px solid',
            borderColor: 'action.disabledBackground',
            borderRadius: '8px !important',
            bgcolor: 'action.hover',
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              minHeight: TOUCH_MIN,
              px: 1.25,
              '& .MuiAccordionSummary-content': {
                my: 1,
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.25,
              },
            }}
          >
            <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
              Couldn’t price ({unpriced.length})
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Missing type / size / fuel / miles — expand for details
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 1, pt: 0, pb: 1 }}>
            {unpriced.map((card, index) => (
              <GraybookCard
                key={`${messageId}:unpriced:${index}`}
                data={card}
                dense
                defaultExpanded={false}
              />
            ))}
          </AccordionDetails>
        </Accordion>
      ) : null}
    </Box>
  )
}
